import { FieldArray, FieldArrayRenderProps, FormikValues, useFormikContext } from 'formik'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Api from 'src/api'
import { ArrowDown } from 'src/assets/icons/customIcons/ArrowDown'
import { Plus } from 'src/assets/icons/customIcons/Plus'
import { DeleteIcon } from 'src/assets/icons/customIcons/upload-icons/Delete'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Button } from 'src/tailwind/components/Button'
import { Form } from 'src/views/components/forms/formik/Form'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Swal from 'sweetalert2'
import { useSection, useSectionData } from './SectionContext'
import {
  buildBodyWithSemesters,
  getSemestersInitialValues,
  submitSection,
  type SemesterFormValues,
} from './sectionFormState'
import { TabFormErrors } from './sectionTabForm'

interface Mentor {
  readonly profileId: string
  readonly quota: number
}

interface Semester {
  readonly semester: string
  readonly amount: number
  readonly mentors: Mentor[]
}

interface FormValues {
  readonly semesters: Semester[]
}

interface SelectOption {
  readonly value: string
  readonly label: string
}

const EMPTY_MENTOR: Mentor = { profileId: '', quota: 0 }
const EMPTY_SEMESTER: Semester = { semester: '', amount: 0, mentors: [] }

export default function AdministratorPortfolioSectionSemesters(): JSX.Element {
  const section = useSectionData()
  const { mutate } = useSection()
  const headers = useAuthenticatedHeaders()
  const navigate = useNavigate()
  const locale = useLocale()
  const t = useTranslatable()
  const { id } = useParams<{ id: string }>()

  const initialValues = React.useMemo(() => getSemestersInitialValues(section), [section])

  const handleSubmit = React.useCallback(
    async (values: { semesters: SemesterFormValues[] }) => {
      const body = buildBodyWithSemesters(section, values.semesters)
      await submitSection({
        headers,
        id,
        body,
        navigate,
        locale,
        mutate,
        t,
      })
    },
    [section, headers, id, navigate, locale, mutate, t]
  )

  return (
    <Form initialValues={initialValues} enableReinitialize onSubmit={handleSubmit} classNames={{ form: 'w-full' }}>
      <SemestersFormContent />
    </Form>
  )
}

function SemestersFormContent(): JSX.Element {
  const t = useTranslatable()
  const { values } = useFormikContext<FormValues>()
  const headers = useAuthenticatedHeaders()
  const { data: portfolioListsSemesters } = useApi({
    endpoint: Api.getAdministratorPortfolioListsStudentSemesters,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  const { data: portfolioListsMentors } = useApi({
    endpoint: Api.getAdministratorPortfolioListsMentors,
    params: React.useMemo(() => ({ headers }), [headers]),
  })
  // Memoize options to prevent recreation on every render
  const semestersOptions = React.useMemo<SelectOption[]>(
    () => portfolioListsSemesters?.map((s) => ({ value: s.id.toString(), label: s.name })) ?? [],
    [portfolioListsSemesters]
  )

  const mentorsOptions = React.useMemo<SelectOption[]>(
    () => portfolioListsMentors?.map((m) => ({ value: m.id.toString(), label: `${m.name} (${m.persNum})` })) ?? [],
    [portfolioListsMentors]
  )

  const confirmDelete = React.useCallback(
    async (index: number, remove: (index: number) => void) => {
      const result = await Swal.fire({
        title: t('common:do_you_really_want_to_delete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#0D6EFD',
        cancelButtonColor: '#6C757D',
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
      })
      if (result.isConfirmed) remove(index)
    },
    [t]
  )

  if (!values.semesters?.length) {
    return (
      <div className="mt-6 flex w-full flex-col items-center gap-4 py-8">
        <TabFormErrors />
        <FieldArray name="semesters">
          {({ push }: FieldArrayRenderProps) => (
            <Button
              variant="blue"
              type="button"
              className="flex items-center gap-2"
              onClick={() => push(EMPTY_SEMESTER)}
            >
              <Plus color="white" />
              <span>{t(['სემესტრის დამატება', 'Add Semester'])}</span>
            </Button>
          )}
        </FieldArray>
        <SemestersSaveButton />
      </div>
    )
  }

  return (
    <div className="mt-6 flex w-full flex-col gap-4">
      <TabFormErrors />
      <FieldArray name="semesters">
        {({ remove, push }: FieldArrayRenderProps) => (
          <>
            {values.semesters.map((semester, index) => (
              <SemesterCard
                key={index}
                index={index}
                semester={semester}
                semestersOptions={semestersOptions}
                mentorsOptions={mentorsOptions}
                onDelete={() => confirmDelete(index, remove)}
                onDeleteMentor={confirmDelete}
              />
            ))}
            <Button
              variant="blue"
              type="button"
              className="flex w-full items-center justify-center gap-2"
              onClick={() => push(EMPTY_SEMESTER)}
            >
              <Plus color="white" />
              <span>{t(['სემესტრის დამატება', 'Add Semester'])}</span>
            </Button>
          </>
        )}
      </FieldArray>
      <SemestersSaveButton />
    </div>
  )
}

interface SemesterCardProps {
  readonly index: number
  readonly semester: Semester
  readonly semestersOptions: readonly SelectOption[]
  readonly mentorsOptions: readonly SelectOption[]
  readonly onDelete: () => void
  readonly onDeleteMentor: (index: number, remove: (index: number) => void) => void
}

const SemesterCard = React.memo(function SemesterCard({
  index,
  semester,
  semestersOptions,
  mentorsOptions,
  onDelete,
  onDeleteMentor,
}: SemesterCardProps): JSX.Element {
  const t = useTranslatable()
  const [isExpanded, setIsExpanded] = React.useState(true)
  const toggleExpand = React.useCallback(() => setIsExpanded((prev) => !prev), [])
  const handleDelete = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onDelete()
    },
    [onDelete]
  )

  return (
    <div className="overflow-hidden rounded-card bg-card shadow-sm">
      {/* Header */}
      <div
        className="flex cursor-pointer items-center justify-between gap-4 bg-gradient-to-r from-card to-card/80 p-4 transition-colors hover:from-card/90"
        onClick={toggleExpand}
      >
        <div className="flex items-center gap-3">
          <div
            className={`flex size-8 items-center justify-center rounded-full bg-primaryBlue/10 transition-transform duration-300 ${
              isExpanded ? 'rotate-0' : '-rotate-90'
            }`}
          >
            <ArrowDown bold />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-primaryTextColor">
              {t(['სემესტრი', 'Semester'])} {semester.semester || '#' + (index + 1)}
            </span>
            <span className="text-xs text-captionColor">
              {t('portfolio:work_count')}: {semester.amount}
            </span>
          </div>
        </div>
        <Button variant="red" type="button" onClick={handleDelete} className="shrink-0">
          <DeleteIcon color="white" />
        </Button>
      </div>

      {/* Body */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 border-t border-borderGrey p-4">
            <div className="flex w-full flex-wrap items-end justify-between gap-4">
              <div className="w-full min-w-[130px] lg:w-[48%]">
                <SelectInput
                  name={`semesters[${index}].semester`}
                  label={t(['სემესტრი', 'Semester'])}
                  options={semestersOptions}
                  required
                />
              </div>
              <div className="w-full min-w-[130px] lg:w-[48%]">
                <TextInput
                  type="number"
                  name={`semesters[${index}].amount`}
                  label={t('portfolio:work_count')}
                  required
                />
              </div>
            </div>

            <FieldArray name={`semesters[${index}].mentors`}>
              {({ remove, push }: FieldArrayRenderProps) => (
                <div className="flex flex-col gap-3 rounded-lg border border-borderGrey bg-card/50 p-4">
                  <MentorList
                    semesterIndex={index}
                    mentors={semester.mentors}
                    mentorsOptions={mentorsOptions}
                    onDelete={(mentorIndex) => onDeleteMentor(mentorIndex, remove)}
                  />
                  <Button
                    variant="blue"
                    type="button"
                    className="flex w-full items-center justify-center gap-2"
                    onClick={() => push(EMPTY_MENTOR)}
                  >
                    <Plus color="white" />
                    <span>{t(['მენტორის დამატება', 'Add Mentor'])}</span>
                  </Button>
                </div>
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </div>
  )
})

interface MentorListProps {
  readonly semesterIndex: number
  readonly mentors: readonly Mentor[]
  readonly mentorsOptions: readonly SelectOption[]
  readonly onDelete: (index: number) => void
}

function MentorList({ semesterIndex, mentors, mentorsOptions, onDelete }: MentorListProps): JSX.Element {
  const t = useTranslatable()

  // Build Set of selected IDs once for O(1) lookups
  const selectedIdsSet = React.useMemo(() => new Set(mentors.map((m) => m.profileId)), [mentors])

  // Memoize filtered options per mentor using a single computation
  const optionsPerMentor = React.useMemo(() => {
    return mentors.map((mentor) => {
      const currentId = mentor.profileId
      return mentorsOptions.filter((opt) => opt.value === currentId || !selectedIdsSet.has(opt.value))
    })
  }, [mentors, mentorsOptions, selectedIdsSet])

  if (mentors.length === 0) {
    return (
      <div className="flex items-center justify-center py-4 text-sm text-captionColor">
        {t(['მენტორები არ არის დამატებული', 'No mentors added yet'])}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {mentors.map((_, mentorIndex) => (
        <MentorRow
          key={mentorIndex}
          semesterIndex={semesterIndex}
          mentorIndex={mentorIndex}
          options={optionsPerMentor[mentorIndex] ?? []}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}

interface MentorRowProps {
  readonly semesterIndex: number
  readonly mentorIndex: number
  readonly options: readonly SelectOption[]
  readonly onDelete: (index: number) => void
}

const MentorRow = React.memo(function MentorRow({
  semesterIndex,
  mentorIndex,
  options,
  onDelete,
}: MentorRowProps): JSX.Element {
  const t = useTranslatable()
  const handleDelete = React.useCallback(() => onDelete(mentorIndex), [onDelete, mentorIndex])

  return (
    <div className="flex w-full flex-wrap items-end justify-between gap-4 rounded-lg bg-card p-3">
      <div className="w-full min-w-[130px] lg:w-[48%]">
        <SelectInput
          name={`semesters[${semesterIndex}].mentors[${mentorIndex}].profileId`}
          label={t('portfolio:mentor')}
          options={options}
          required
        />
      </div>
      <div className="flex w-full min-w-[130px] items-start justify-between gap-2 lg:w-[48%]">
        <div className="flex-1">
          <TextInput
            type="number"
            name={`semesters[${semesterIndex}].mentors[${mentorIndex}].quota`}
            label={t('portfolio:quota')}
            required
          />
        </div>
        <Button variant="red" type="button" onClick={handleDelete} className="mt-[31px]">
          <DeleteIcon color="white" />
        </Button>
      </div>
    </div>
  )
})

function SemestersSaveButton(): JSX.Element | null {
  const t = useTranslatable()
  const canManagePortfolio = useAdminAccess().canManage('portfolio')
  const formik = useFormikContext<FormikValues>()

  if (!canManagePortfolio) return null
  return (
    <div className="my-4 flex flex-wrap justify-end">
      <Button type="submit" className="ml-2" variant="red" disabled={formik.isSubmitting || !formik.dirty}>
        {t('common:save')}
      </Button>
    </div>
  )
}
